/*
 * File: cron-classic.ts
 * File Created: 2024-09-13 09:29:09
 * Author: Val Liu <valuis0429@gmail.com>
 *
 * -----
 * Last Modified: 2024-10-14 09:28:11
 * Modified By: Val Liu
 * -----
 */

import { StringHelper } from "@root/core/utilities/string-helper";
import { Component, Emit, Prop, Vue, Watch } from "vue-property-decorator";

export interface IInputCronClassicData {
    "enable-second": boolean;
    "enable-minute": boolean;
    "enable-hour": boolean;
    "enable-day": boolean;
    "enable-month": boolean;
    "enable-year": boolean;
}
const defaultData: IInputCronClassicData = {
    "enable-second": false,
    "enable-minute": false,
    "enable-hour": false,
    "enable-day": true,
    "enable-month": true,
    "enable-year": true,
};

@Component
export default class CronClassic extends Vue {
    @Prop()
    expression: any;

    @Prop({ type: String, default: "w_InputCronPlaceholder" })
    placeholder!: string;

    @Prop({ type: Object, required: false })
    data!: IInputCronClassicData;
    get mergedData() {
        return {
            ...defaultData,
            ...this.data,
        };
    }

    @Emit("change")
    doChange() {
        return this.cron;
    }

    @Emit("text")
    doText() {
        return this.readText();
    }

    /// emit on value changed
    @Watch("cron")
    onCronChanged(value) {
        this.doChange();
        this.doText();
    }
    @Watch("expression")
    onExpressionChanged(value) {
        this.resolveExpression();
        this.doText();
    }

    /// private helpers
    second = {
        cronEvery: "",
        incrementStart: "3",
        incrementIncrement: "5",
        rangeStart: "",
        rangeEnd: "",
        specificSpecific: [],
    };
    i18n = {
        second: {
            1: "w_InputCronSecondEvery",
            2: "w_InputCronSecondCriteria",
            3: "w_InputCronSecondSpecific",
            4: "w_InputCronSecondBetween",
        },
        minute: {
            1: "w_InputCronMinuteEvery",
            2: "w_InputCronMinuteCriteria",
            3: "w_InputCronMinuteSpecific",
            4: "w_InputCronMinuteBetween",
        },
        hour: {
            1: "w_InputCronHourEvery",
            2: "w_InputCronHourCriteria",
            3: "w_InputCronHourSpecific",
            4: "w_InputCronHourBetween",
        },
        day: {
            1: "w_InputCronDayEvery",
            3: "w_InputCronDayCriteria",
            4: "w_InputCronDayOfWeek",
            5: "w_InputCronDayOfMonth",
            6: "w_InputCronDayLastDayOfMonth",
            7: "w_InputCronDayLastWeekdayOfMonth",
            8: "w_InputCronDayLastDayOfWeek",
            9: "w_InputCronDayBeforeLastDayOfMonth",
            10: "w_InputCronDayNearestWeekday",
            11: "w_InputCronDayNumberedDayOfWeek",
        },
        month: {
            1: "w_InputCronMonthEvery",
            2: "w_InputCronMonthCriteria",
            3: "w_InputCronMonthSpecific",
            4: "w_InputCronMonthBetween",
        },
        year: {
            1: "w_InputCronYearEvery",
            2: "w_InputCronYearCriteria",
            3: "w_InputCronYearSpecific",
            4: "w_InputCronYearBetween",
        },
    };

    minute = {
        cronEvery: "",
        incrementStart: "3",
        incrementIncrement: "5",
        rangeStart: "",
        rangeEnd: "",
        specificSpecific: [],
    };

    hour = {
        cronEvery: "",
        incrementStart: "3",
        incrementIncrement: "5",
        rangeStart: "",
        rangeEnd: "",
        specificSpecific: [],
    };

    day = {
        cronEvery: "",
        incrementStart: "1",
        incrementIncrement: "1",
        rangeStart: "",
        rangeEnd: "",
        specificSpecific: [] as any[],
        cronLastSpecificDomDay: 1,
        cronDaysBeforeEomMinus: "",
        cronDaysNearestWeekday: "",
    };

    week = {
        cronEvery: "",
        incrementStart: "1",
        incrementIncrement: "1",
        specificSpecific: [] as any[],
        cronNthDayDay: 1,
        cronNthDayNth: "1",
    };

    month = {
        cronEvery: "",
        incrementStart: "3",
        incrementIncrement: "5",
        rangeStart: "",
        rangeEnd: "",
        specificSpecific: [] as any[],
    };

    year = {
        cronEvery: "",
        incrementStart: "2017",
        incrementIncrement: "1",
        rangeStart: "",
        rangeEnd: "",
        specificSpecific: [],
    };

    output = {
        second: "",
        minute: "",
        hour: "",
        day: "",
        month: "",
        Week: "",
        year: "",
    };

    /// readText
    readText() {
        if (this.expression == null) return null;

        const result: string[] = [];

        const handleCommonCase = (type: string): IReadTextCaseValue => {
            let every: boolean = false;
            let data: string | null = null;

            if (this.mergedData[`enable-${type}`] == false) {
                return null;
            }

            switch (this[type].cronEvery) {
                case "2":
                    /// increment
                    break;
                case "3":
                    /// specific
                    const spec = this[type].specificSpecific;
                    data = spec?.length == 1 ? spec[0] : null;
                    break;
                case "4":
                    /// range
                    data = `${this[type].rangeStart}-${this[type].rangeEnd}`;
                    break;
                case "1":
                default:
                    every = true;
                    break;
            }
            const capType = StringHelper.capitalize(type);
            const msg = every
                ? this.$_(`w_InputCronReadText${capType}Every`)
                : data == null
                  ? this.$_(`w_InputCronReadText${capType}Sp`)
                  : this.$_(`w_InputCronReadText${capType}`, [data]);

            return {
                msg,
                every,
            };
        };

        const handleMonthCase = (): IReadTextCaseValue => {
            const type = "month";
            let every: boolean = false;
            let msg: string | null = null;
            let data: string | null = null;

            if (this.mergedData[`enable-${type}`] == false) {
                return null;
            }

            switch (this[type].cronEvery) {
                case "2":
                    /// increment
                    break;
                case "3":
                    /// specific
                    const spec = this[type].specificSpecific;
                    /// normal case
                    data = spec?.length == 1 ? spec[0] : null;
                    /// even and odd
                    if (spec?.length == 6) {
                        do {
                            let odd = spec.reduce((final, value) => {
                                if (final === false) return final;
                                if (value % 2 !== 1) return false;
                                return final;
                            }, true);
                            if (odd === true) {
                                msg = this.$_("w_InputCronReadTextMonthOdd");
                                break;
                            }

                            let even = spec.reduce((final, value) => {
                                if (final === false) return final;
                                if (value % 2 !== 0) return false;
                                return final;
                            }, true);
                            if (even === true) {
                                msg = this.$_("w_InputCronReadTextMonthEven");
                                break;
                            }
                        } while (0);
                    }
                    break;
                case "4":
                    /// range
                    data = `${this[type].rangeStart}-${this[type].rangeEnd}`;
                    break;
                case "1":
                default:
                    every = true;
                    break;
            }
            const capType = StringHelper.capitalize(type);
            if (msg == null) {
                msg = every
                    ? this.$_(`w_InputCronReadText${capType}Every`)
                    : data == null
                      ? this.$_(`w_InputCronReadText${capType}Sp`)
                      : this.$_(`w_InputCronReadText${capType}`, [data]);
            }

            return {
                msg,
                every,
            };
        };

        const handleDayCase = (): IReadTextCaseValue => {
            const type = "day";
            let every: boolean = false;
            let data: string | null = null;
            let msg: string | null = null;

            if (this.mergedData[`enable-${type}`] == false) {
                return null;
            }

            switch (this[type].cronEvery) {
                case "3":
                    /// increment
                    break;
                case "4":
                    let result = this.week.specificSpecific.map((value) => {
                        return this.$_(`w_DateTime_ShortWeekDay${value - 1}`);
                    });
                    msg = this.$_("w_InputCronDayOfWeek", [result.join("/")]);
                    break;
                case "5":
                    /// show only one day
                    if (this.day.specificSpecific?.length == 1) {
                        msg = this.$_(
                            "w_InputCronReadTextDay",
                            this.day.specificSpecific,
                        );
                    }
                    break;
                case "6":
                    msg = this.$_(this.i18n.day[6], {
                        w_EveryMonth: "",
                    });
                    break;
                case "7":
                    msg = this.$_(this.i18n.day[7], {
                        w_EveryMonth: "",
                    });
                    break;
                case "8":
                    msg = this.$_(this.i18n.day[8], {
                        w_EveryMonth: "",
                        0: this.$_(
                            `w_DateTime_ShortWeekDay${
                                this.day.cronLastSpecificDomDay - 1
                            }`,
                        ),
                    });
                    break;
                case "9":
                    msg = this.$_(this.i18n.day[9], {
                        w_EveryMonth: "",
                        0: this.day.cronDaysBeforeEomMinus,
                    });
                    break;
                case "10":
                    msg = this.$_(this.i18n.day[10], {
                        w_EveryMonth: "",
                        0: this.day.cronDaysNearestWeekday,
                    });
                    break;
                case "11":
                    msg = this.$_(this.i18n.day[11], {
                        w_EveryMonth: "",
                        0: this.week.cronNthDayNth,
                        1: this.$_(
                            `w_DateTime_ShortWeekDay${this.week.cronNthDayDay - 1}`,
                        ),
                    });
                    break;
                case "1":
                default:
                    every = true;
                case "2":
                    /// don't handle 2
                    break;
            }
            const capType = StringHelper.capitalize(type);
            if (msg == null) {
                msg = every
                    ? this.$_(`w_InputCronReadText${capType}Every`)
                    : data == null
                      ? this.$_(`w_InputCronReadText${capType}Sp`)
                      : null;
            }

            return {
                msg,
                every,
            };
        };

        let lastEvery: boolean = false;
        const handleMsgAndEvery = (value: IReadTextCaseValue) => {
            if (value == null) return;
            const { msg, every } = value;

            if (every == true) {
                if (lastEvery == true) result.pop();
                lastEvery = true;
            } else {
                /// don't show every if any message exists.
                // if (lastEvery == true) result.pop();
                lastEvery = false;
            }
            if (msg != null) result.push(msg);
        };

        /// year
        year: {
            const $case = handleCommonCase("year");
            handleMsgAndEvery($case);
        }
        month: {
            const $case = handleMonthCase();
            handleMsgAndEvery($case);
        }
        day: {
            const $case = handleDayCase();
            handleMsgAndEvery($case);
        }
        hour: {
            const $case = handleCommonCase("hour");
            handleMsgAndEvery($case);
        }
        minute: {
            const $case = handleCommonCase("minute");
            handleMsgAndEvery($case);
        }
        second: {
            const $case = handleCommonCase("second");
            handleMsgAndEvery($case);
        }

        return result.join(" ");
    }

    /// limits
    yearNow = new Date().getFullYear();
    yearMin = this.yearNow - 2;
    yearMax = this.yearMin + 100;

    /// resolved text
    get secondsText() {
        let seconds = "";
        let cronEvery = this.second.cronEvery;
        switch (cronEvery.toString()) {
            case "1":
                seconds = "*";
                break;
            case "2":
                seconds =
                    this.second.incrementStart + "/" + this.second.incrementIncrement;
                break;
            case "3":
                this.second.specificSpecific.map((val) => {
                    seconds += val + ",";
                });
                seconds = seconds.slice(0, -1);
                break;
            case "4":
                seconds = this.second.rangeStart + "-" + this.second.rangeEnd;
                break;
        }
        return this.mergedData["enable-second"] ? seconds || "*" : "0";
    }

    get minutesText() {
        let minutes = "";
        let cronEvery = this.minute.cronEvery;
        switch (cronEvery.toString()) {
            case "1":
                minutes = "*";
                break;
            case "2":
                minutes =
                    this.minute.incrementStart + "/" + this.minute.incrementIncrement;
                break;
            case "3":
                this.minute.specificSpecific.map((val) => {
                    minutes += val + ",";
                });
                minutes = minutes.slice(0, -1);
                break;
            case "4":
                minutes = this.minute.rangeStart + "-" + this.minute.rangeEnd;
                break;
        }
        return this.mergedData["enable-minute"] ? minutes || "*" : "0";
    }

    get hoursText() {
        let hours = "";
        let cronEvery = this.hour.cronEvery;
        switch (cronEvery.toString()) {
            case "1":
                hours = "*";
                break;
            case "2":
                hours = this.hour.incrementStart + "/" + this.hour.incrementIncrement;
                break;
            case "3":
                this.hour.specificSpecific.map((val) => {
                    hours += val + ",";
                });
                hours = hours.slice(0, -1);
                break;
            case "4":
                hours = this.hour.rangeStart + "-" + this.hour.rangeEnd;
                break;
        }
        return this.mergedData["enable-hour"] ? hours || "*" : "0";
    }

    get daysText() {
        let days = "";
        let cronEvery = this.day.cronEvery;
        switch (cronEvery.toString()) {
            case "1":
                break;
            case "2":
            case "4":
            case "8":
            case "11":
                days = "?";
                break;
            case "3":
                days = this.day.incrementStart + "/" + this.day.incrementIncrement;
                break;
            case "5":
                this.day.specificSpecific.map((val) => {
                    days += val + ",";
                });
                days = days.slice(0, -1);
                break;
            case "6":
                days = "L";
                break;
            case "7":
                days = "LW";
                break;
            case "9":
                days = "L-" + this.day.cronDaysBeforeEomMinus;
                break;
            case "10":
                days = this.day.cronDaysNearestWeekday + "W";
                break;
        }
        return this.mergedData["enable-day"] ? days || "*" : "1";
    }

    get weeksText() {
        let weeks = "";
        let cronEvery = this.day.cronEvery;
        switch (cronEvery.toString()) {
            case "1":
            case "3":
            case "5":
                weeks = "?";
                break;
            case "2":
                weeks = this.week.incrementStart + "/" + this.week.incrementIncrement;
                break;
            case "4":
                this.week.specificSpecific.map((val) => {
                    weeks += val + ",";
                });
                weeks = weeks.slice(0, -1);
                break;
            case "8":
                weeks = this.day.cronLastSpecificDomDay + "L";
                break;
            case "6":
            case "7":
            case "9":
            case "10":
                weeks = "?";
                break;
            case "11":
                weeks = this.week.cronNthDayDay + "#" + this.week.cronNthDayNth;
                break;
        }
        return this.mergedData["enable-day"] ? weeks || "*" : "?";
    }

    get monthsText() {
        let months = "";
        let cronEvery = this.month.cronEvery;
        switch (cronEvery.toString()) {
            case "1":
                months = "*";
                break;
            case "2":
                months = this.month.incrementStart + "/" + this.month.incrementIncrement;
                break;
            case "3":
                this.month.specificSpecific.map((val) => {
                    months += val + ",";
                });
                months = months.slice(0, -1);
                break;
            case "4":
                months = this.month.rangeStart + "-" + this.month.rangeEnd;
                break;
        }
        return this.mergedData["enable-month"] ? months || "*" : "1";
    }

    get yearsText() {
        let years = "";
        let cronEvery = this.year.cronEvery;
        switch (cronEvery.toString()) {
            case "1":
                years = "*";
                break;
            case "2":
                years = this.year.incrementStart + "/" + this.year.incrementIncrement;
                break;
            case "3":
                this.year.specificSpecific.map((val) => {
                    years += val + ",";
                });
                years = years.slice(0, -1);
                break;
            case "4":
                years = this.year.rangeStart + "-" + this.year.rangeEnd;
                break;
        }
        return this.mergedData["enable-year"] ? years || "*" : "*";
    }

    get cron() {
        //if (this.expression == undefined) return null;
        // if (this.year.cronEvery == null && this.month.cronEvery == null && this.day.cronEvery == null)
        const isNull = ["year", "month", "day", "hour", "minute", "second"].reduce(
            (final, key) => {
                if (final == false) return final;
                /// not: empty string or null or undefined
                if (this[key].cronEvery) return false;
                return final;
            },
            true,
        );
        if (isNull) return null;

        return `${this.secondsText} ${this.minutesText} ${this.hoursText} ${this.daysText} ${this.monthsText} ${this.weeksText} ${this.yearsText}`;
    }

    /// parse the expression
    exps = [
        { type: "second", expression: "" },
        { type: "minute", expression: "" },
        { type: "hour", expression: "" },
        { type: "day", expression: "" },
        { type: "month", expression: "" },
        { type: "Week", expression: "" },
        { type: "year", expression: "" },
    ];
    resolveExpression() {
        if (!this.expression?.length) return;
        let expList = this.expression.split(" ");
        if (expList.length < 7) {
            new Error("Incorrect expression format");
        }
        for (let i = 0; i < expList.length; i++) {
            this.exps[i].expression = expList[i];
        }
        this.exps.forEach((exp) => {
            this.output[exp.type] = exp.expression;
            switch (exp.type) {
                case "year":
                case "month":
                case "hour":
                case "minute":
                case "second":
                    commonParser.call(this, this[exp.type], exp.expression);
                    return;
                case "Week":
                    resolveWeek.call(this, exp.expression);
                    return;
                case "day":
                    resolveDay.call(this, exp.expression);
                    return;
            }
        });
    }

    /// private helpers
    private mounted() {
        this.resolveExpression();
        this.doText();
    }

    aryTxt(type: string, label: number, index: number) {
        const regex = /\{[0-9a-z]*\}/gi;
        // return this.$_(key).split(regex)[index] ?? "";
        return this.$_(this.i18n[type][label]).split(regex)[index] ?? "";
    }

    aryText(key: string, index: number) {
        const regex = /\{[0-9a-z]*\}/gi;
        return this.$_(key).split(regex)[index] ?? "";
    }
}

type IReadTextCaseValue = { msg: string | null; every: boolean } | null;

/// expression parser
function commonParser(this: CronClassic, expressionType, str) {
    if (str == "*") {
        resolveStar.call(this, expressionType);
    } else if (str.indexOf("-") >= 0) {
        resolveLine.call(this, expressionType, str);
    } else if (str.indexOf("/") >= 0) {
        resolveSlash.call(this, expressionType, str);
    } else {
        resolveComma.call(this, expressionType, str);
    }
}

function resolveWeek(this: CronClassic, str) {
    if (str.indexOf("/") >= 0) {
        this.day.cronEvery = "2";
        resolveSlash.call(this, this.week, str);
    } else if (str.indexOf(",") >= 0) {
        this.day.cronEvery = "4";
        resolveComma.call(this, this.week, str);
    } else if (str.indexOf("#") >= 0) {
        this.day.cronEvery = "11";
        let range = str.split("#");
        this.week.cronNthDayDay = Number(range[0]);
        this.week.cronNthDayNth = range[1];
    }
}

function resolveDay(this: CronClassic, str) {
    if (str == "*") {
        resolveStar.call(this, this.day);
    } else if (str == "?") {
        /// do nothing
    } else if (str.indexOf("/") >= 0) {
        resolveSlash.call(this, this.day, str, "3");
    } else if (str == "L") {
        this.day.cronEvery = "6";
    } else if (str == "LW") {
        this.day.cronEvery = "7";
    } else if (/\dL/.test(str)) {
        this.day.cronEvery = "8";
        this.day.cronLastSpecificDomDay = Number(str.match(/(\d)L/)[1]);
    } else if (/L-\d+/.test(str)) {
        this.day.cronEvery = "9";
        this.day.cronDaysBeforeEomMinus = Number(str.match(/L-(\d+)/)[1]) as any;
    } else if (/\d+W/.test(str)) {
        this.day.cronEvery = "10";
        this.day.cronDaysNearestWeekday = Number(str.match(/(\d+)W/)[1]) as any;
    } else {
        resolveComma.call(this, this.day, str, "5");
    }
}

function resolveSlash(this: CronClassic, expressionObj, expression, type = "2") {
    expressionObj.cronEvery = type;
    let range = expression.split("/");
    expressionObj.incrementStart = range[0];
    expressionObj.incrementIncrement = range[1];
}

function resolveLine(this: CronClassic, expressionObj, expression, type = "4") {
    expressionObj.cronEvery = type;
    let range = expression.split("-");
    expressionObj.rangeStart = range[0];
    expressionObj.rangeEnd = range[1];
}

function resolveComma(this: CronClassic, expressionObj, expression, type = "3") {
    expressionObj.cronEvery = type;
    expressionObj.specificSpecific = expression.split(",");
}

function resolveStar(this: CronClassic, expressionObj, type = "1") {
    expressionObj.cronEvery = type;
}
